import React from 'react';
import { Link } from 'react-router-dom';
import { Mail } from 'lucide-react';
import { motion } from 'framer-motion';

const teamMembers = [
  {
    name: "Albanda Mursalzade",
    title: "Founder & CEO",
    imageUrl: "/images/albanda.jpg",
    link: "/team/albanda",
    bio: "Visionary leader with 10+ years in software and cloud computing. Drives scalable innovation.",
    email: "albanda@example.com",
  },
  {
    name: "Kamran Baghirzada",
    title: "Chief Technology Officer (CTO)",
    imageUrl: "/images/kamran.jpg",
    link: "/team/kamran",
    bio: "Tech strategist focused on cloud-native development and DevOps for secure, scalable systems.",
    email: "kamran@example.com",
  },
];

const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.2,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { 
      type: 'spring',
      stiffness: 100,
      damping: 20,
    },
  },
};

const TeamSection = () => {
  return (
    <section id="team" className="relative py-8 px-4 overflow-hidden">
      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-8">
          <h2 className="text-3xl md:text-4xl font-mono font-bold text-white mb-2">
            Our Team
          </h2>
          <div className="h-1 w-16 bg-gradient-to-r from-green-500 to-emerald-500 mx-auto rounded-full mb-4" />
          <p className="text-green-400/80 text-base max-w-2xl mx-auto">
            Meet the innovative minds behind our tech success.
          </p>
        </div>

        {/* Team Grid with Framer Motion */}
        <motion.div
          className="grid gap-4 sm:grid-cols-2 md:grid-cols-3"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {teamMembers.map((member, index) => (
            <motion.div
              key={index}
              className="group relative"
              variants={cardVariants}
              whileHover={{
                scale: 1.05,
                transition: { type: 'spring', stiffness: 200, damping: 15 },
              }}
            >
              <div className="relative rounded-lg bg-white/10 backdrop-blur-md p-3 transition-all duration-300 hover:shadow-lg hover:shadow-green-500/20">
                {/* Member Image */}
                <div className="flex justify-center mb-3">
                  <img
                    src={member.imageUrl}
                    alt={member.name}
                    className="w-20 h-20 rounded-full object-cover transition-transform duration-300"
                  />
                </div>

                {/* Member Info */}
                <div className="text-center">
                  <h3 className="text-lg font-mono font-bold text-white mb-1">
                    {member.name}
                  </h3>
                  <p className="text-green-400 font-medium text-xs mb-1">
                    {member.title}
                  </p>
                  <p className="text-gray-300 text-xs mb-2">
                    {member.bio}
                  </p>

                  {/* Email Link */}
                  <div className="flex justify-center mb-2">
                    <a
                      href={`mailto:${member.email}`}
                      className="inline-flex items-center text-gray-400 hover:text-green-400 transition-colors gap-1 text-xs"
                    >
                      <Mail size={16} />
                      <span>Contact</span>
                    </a>
                  </div>

                  {/* Profile Link */}
                  <Link
                    to={member.link}
                    className="inline-block px-3 py-1 text-xs font-medium text-white bg-green-600 rounded hover:bg-green-500 transition-colors"
                  >
                    View Profile
                  </Link>
                </div>

                {/* Decorative Gradient Bars */}
                <div className="absolute top-0 left-0 h-1 w-0 bg-gradient-to-r from-green-500 to-emerald-500 transition-all duration-300 group-hover:w-full" />
                <div className="absolute bottom-0 right-0 h-1 w-0 bg-gradient-to-r from-emerald-500 to-green-500 transition-all duration-300 group-hover:w-full" />
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default TeamSection;